import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { REHYDRATE } from 'redux-persist'

import { logout } from '~/auth/slices/auth.slice'
import { RootState } from '~/redux/store'
import { UserType } from '~/types/user-info-types'
import { TopicSearch } from '~/components/Search/types'
import {
  DetailsFields,
  DetailsFieldsErrors,
  OnboardingState,
  OnboardingV2State,
  QuestionStructure,
  UsOnboardingSteps,
} from '~/onboarding/onboarding.types'
import { UserCategory, UsOnboardingOnboardingStep } from '~/get-started/us-onboarding/consts/types'

const INIT_DETAILS_FIELDS = {
  nickname: '',
  email: '',
  birthYear: null,
  country: '',
  wantsNewsletter: false,
  skill: null,
}

const INIT_DETAILS_FIELDS_ERRORS = {
  nickname: '',
  email: '',
  birthYear: '',
  country: '',
  wantsNewsletter: '',
  skill: '',
}

const initialState: OnboardingState = {
  chosenAccountType: null,
  details: INIT_DETAILS_FIELDS,
  errors: INIT_DETAILS_FIELDS_ERRORS,
  showHomMethods: false,
  userRole: 'other',
  currentStepIndex: 0,
  isPreSignup: false,
  usOnboarding: {
    category: UserCategory.STUDENT,
    currentStep: UsOnboardingOnboardingStep.BECOME_A_MATH_MASTER,
    selectedOption: 0,
    steps: {} as Record<UsOnboardingOnboardingStep, QuestionStructure | QuestionStructure[]>,
  },
  onboardingV2: {
    grade: null,
    problem: null,
    breakdown: null,
    tried: null,
  },
}

const slice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    setAccountType: (state, action: PayloadAction<UserType | null>) => ({
      ...state,
      chosenAccountType: action.payload,
    }),

    setDetailsFieldValue: (state, action: PayloadAction<{ fieldName: string; value: number | string | boolean }>) => ({
      ...state,
      details: {
        ...state.details,
        [action.payload.fieldName]: action.payload.value,
      },
    }),

    setFieldErrors: (state, action: PayloadAction<{ fieldName: string; value: string }>) => ({
      ...state,
      errors: {
        ...state.errors,
        [action.payload.fieldName]: action.payload.value,
      },
    }),
    resetDetailsErrors: (state) => ({
      ...state,
      errors: INIT_DETAILS_FIELDS_ERRORS,
    }),

    setPickedTopic: (state, action: PayloadAction<TopicSearch | null>) => ({
      ...state,
      pickedTopic: action.payload,
    }),
    setPickedTaskId: (state, action: PayloadAction<string>) => ({
      ...state,
      pickedTaskId: action.payload,
    }),
    setShowHomMethods: (state, action: PayloadAction<boolean>) => ({
      ...state,
      showHomMethods: action.payload,
    }),
    setUserRole: (state, action: PayloadAction<'student' | 'parent' | 'teacher' | 'other' | null>) => ({
      ...state,
      userRole: action.payload,
      currentStepIndex: 0,
    }),
    setCurrentStepIndex: (state, action: PayloadAction<number>) => ({
      ...state,
      currentStepIndex: action.payload,
    }),
    setIsPreSignup: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isPreSignup: action.payload,
    }),
    setUsOnboarding: (state, action: PayloadAction<UsOnboardingSteps>) => ({
      ...state,
      usOnboarding: {
        ...action.payload,
        steps: action.payload.steps || state.usOnboarding.steps,
      },
    }),
    setOnboardingV2: (state, action: PayloadAction<OnboardingV2State>) => ({
      ...state,
      onboardingV2: {
        ...state.onboardingV2,
        ...action.payload,
      },
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(logout, () => initialState)
    builder.addCase(REHYDRATE as any, (state, action: any) => {
      if (action.key === 'onboarding') {
        return {
          ...state,
          ...action.payload,
        }
      }
      return state
    })
  },
})

export const selectAccountType = (state: RootState): UserType | null => state.onboarding.chosenAccountType
export const selectDetails = (state: RootState): DetailsFields => state.onboarding.details
export const selectErrors = (state: RootState): DetailsFieldsErrors => state.onboarding.errors
export const selectShowHomMethods = (state: RootState): boolean => state.onboarding.showHomMethods
export const selectUserRole = (state: RootState): 'student' | 'parent' | 'teacher' | 'other' | null =>
  state.onboarding.userRole
export const selectCurrentStepIndex = (state: RootState): number => state.onboarding.currentStepIndex
export const selectIsPreSignup = (state: RootState): boolean | undefined => state.onboarding.isPreSignup
export const selectUsOnboarding = (state: RootState): UsOnboardingSteps => state.onboarding.usOnboarding
export const selectOnboardingV2 = (state: RootState): OnboardingV2State => state.onboarding.onboardingV2

export const {
  setAccountType: setAccountTypeActionCreator,
  setDetailsFieldValue: setDetailsFieldValueActionCreator,
  setFieldErrors: setFieldErrorsActionCreator,
  resetDetailsErrors: resetDetailsErrorsActionCreator,
  setShowHomMethods: setShowHomMethodsActionCreator,
  setUserRole: setUserRoleActionCreator,
  setCurrentStepIndex: setCurrentStepIndexActionCreator,
  setIsPreSignup: setIsPreSignupActionCreator,
  setUsOnboarding: setUsOnboardingActionCreator,
  setOnboardingV2: setOnboardingV2ActionCreator,
} = slice.actions

export default slice.reducer
